import { CourseRole, OrgRole, SectionRole } from "./types/roles";
import { InteractionMode, type PagePermissionType } from "./types/websocket-auth";

export enum UserRoleType {
  ORG_ADMIN = "orgAdmin",
  INSTRUCTOR = "instructor",
  STUDENT = "student",
  SECTION_ASSISTANT = "sectionAssistant",
  COURSE_ADMIN = "courseAdmin",
  MP_ADMIN = "mpAdmin",
  RECORDING_AGENT = "recordingAgent",
  UNKNOWN = "unknown",
}

// Capabilities at the page level
export enum PageCapability {
  CAN_VIEW_PAGE = "canViewPage",
  CAN_VIEW_OTHER_USERS_DATA = "canViewOtherUsersData",
  CAN_MAKE_RESPONSE = "canMakeResponse",
  CAN_AUTHOR_PAGE = "canAuthorPage",
}

// Capabilities at the section level
export enum SectionCapability {
  CAN_AUTHOR_SECTION = "canAuthorSection",
  CAN_EDIT_STUDENT_DRAFT = "canEditStudentDraft",
  CAN_CREATE_CHAT_MESSAGE = "canCreateChatMessage",
  CAN_DELETE_CHAT_MESSAGE = "canDeleteChatMessage",
  CAN_PARTICIPATE_IN_SECTION = "canParticipateInSection",
  CAN_PRESENT_SECTION = "canPresentSection",
  CAN_START_AND_STOP_RECORDING = "canStartAndStopRecording",
  IS_VISIBLE_IN_CLASS = "isVisibleInClass",
}

// Capabilities at the org level
export enum OrgCapability {
  CAN_ACCESS_MONITORING_DASHBOARD = "canAccessMonitoringDashboard",
  CAN_AUTHOR_ORG = "canAuthorOrg",
  CAN_OBSERVE_VIRTUAL_CLASS = "canObserveVirtualClass",
  CAN_SEE_ALL_DOCS = "canSeeAllDocs",
  CAN_SEE_ALL_ORG_USERS = "canSeeAllOrgUsers",
}

// Capabilities at the course level
export enum CourseCapability {
  CAN_AUTHOR_COURSE = "canAuthorCourse",
}

export const Capability = {
  ...SectionCapability,
  ...OrgCapability,
  ...CourseCapability,
};

const ROLES_MAP: Record<SectionRole | OrgRole | CourseRole, UserRoleType> = {
  admin: UserRoleType.ORG_ADMIN,
  instructor: UserRoleType.INSTRUCTOR,
  section_assistant: UserRoleType.SECTION_ASSISTANT,
  course_admin: UserRoleType.COURSE_ADMIN,
  student: UserRoleType.STUDENT,
  recording_agent: UserRoleType.RECORDING_AGENT,
  other: UserRoleType.UNKNOWN,
  none: UserRoleType.UNKNOWN,
};

// Course admins get the same section-level capabilities as org admins, but only for
// sections whose course they're assigned to (via CourseUser or CourseGroupingUser).
// Keep this list mirrored with ORG_ADMIN entries below.
const SECTION_CAPABILITIES_MAP: Record<SectionCapability, UserRoleType[]> = {
  [Capability.CAN_AUTHOR_SECTION]: [
    UserRoleType.INSTRUCTOR,
    UserRoleType.SECTION_ASSISTANT,
    UserRoleType.COURSE_ADMIN,
    UserRoleType.MP_ADMIN,
    UserRoleType.ORG_ADMIN,
  ],
  [Capability.CAN_EDIT_STUDENT_DRAFT]: [
    UserRoleType.INSTRUCTOR,
    UserRoleType.SECTION_ASSISTANT,
    UserRoleType.COURSE_ADMIN,
    UserRoleType.MP_ADMIN,
    UserRoleType.ORG_ADMIN,
  ],
  [Capability.CAN_CREATE_CHAT_MESSAGE]: [
    UserRoleType.INSTRUCTOR,
    UserRoleType.SECTION_ASSISTANT,
    UserRoleType.COURSE_ADMIN,
    UserRoleType.MP_ADMIN,
    UserRoleType.ORG_ADMIN,
    UserRoleType.STUDENT,
  ],
  [Capability.CAN_DELETE_CHAT_MESSAGE]: [
    UserRoleType.INSTRUCTOR,
    UserRoleType.SECTION_ASSISTANT,
    UserRoleType.COURSE_ADMIN,
    UserRoleType.MP_ADMIN,
    UserRoleType.ORG_ADMIN,
  ],
  [Capability.CAN_PARTICIPATE_IN_SECTION]: [UserRoleType.STUDENT],
  // For now, only instructors/section assistants can present
  [Capability.CAN_PRESENT_SECTION]: [UserRoleType.INSTRUCTOR, UserRoleType.SECTION_ASSISTANT],
  [Capability.CAN_START_AND_STOP_RECORDING]: [
    UserRoleType.INSTRUCTOR,
    UserRoleType.SECTION_ASSISTANT,
    UserRoleType.COURSE_ADMIN,
    UserRoleType.MP_ADMIN,
    UserRoleType.ORG_ADMIN,
  ],
  [Capability.IS_VISIBLE_IN_CLASS]: [
    UserRoleType.INSTRUCTOR,
    UserRoleType.SECTION_ASSISTANT,
    UserRoleType.STUDENT,
  ],
};

const ORG_CAPABILITIES_MAP: Record<OrgCapability, UserRoleType[]> = {
  // TODO: remove org admin capability once mp admins have a none org role
  [Capability.CAN_ACCESS_MONITORING_DASHBOARD]: [UserRoleType.ORG_ADMIN, UserRoleType.MP_ADMIN],
  [Capability.CAN_AUTHOR_ORG]: [UserRoleType.ORG_ADMIN, UserRoleType.MP_ADMIN],
  [Capability.CAN_SEE_ALL_DOCS]: [UserRoleType.RECORDING_AGENT],
  [Capability.CAN_SEE_ALL_ORG_USERS]: [UserRoleType.MP_ADMIN],
  [Capability.CAN_OBSERVE_VIRTUAL_CLASS]: [UserRoleType.ORG_ADMIN, UserRoleType.MP_ADMIN],
};

const COURSE_CAPABILITIES_MAP: Record<CourseCapability, UserRoleType[]> = {
  [Capability.CAN_AUTHOR_COURSE]: [
    UserRoleType.COURSE_ADMIN,
    UserRoleType.ORG_ADMIN,
    UserRoleType.MP_ADMIN,
  ],
};

export type GetUserRoleParams = {
  isMpAdmin: boolean;
  sectionRole?: SectionRole | null;
  orgRole?: OrgRole | null;
  courseRole?: CourseRole | null;
  isStudentPreview?: boolean;
};

export const getUserRole = ({
  isMpAdmin,
  sectionRole,
  orgRole,
  courseRole,
  isStudentPreview = false,
}: GetUserRoleParams): UserRoleType => {
  if (isStudentPreview) {
    return UserRoleType.STUDENT;
  }

  const role = sectionRole || courseRole || orgRole;
  // MP Admin users might have a "none" role in the org, which allows them to have full MP Admin capabilities.
  if (role && role !== OrgRole.NONE) {
    return ROLES_MAP[role];
  }

  return isMpAdmin ? UserRoleType.MP_ADMIN : UserRoleType.UNKNOWN;
};

// Section capabilities
export const hasSectionCapability = (userRole: UserRoleType, capability: SectionCapability) =>
  SECTION_CAPABILITIES_MAP[capability].includes(userRole);

export const getSectionCapabilities = (userRole: UserRoleType) => {
  return Object.fromEntries(
    Object.values(SectionCapability).map((capability) => [
      capability,
      hasSectionCapability(userRole, capability),
    ]),
  ) as Record<SectionCapability, boolean>;
};

// Org capabilities
export const hasOrgCapability = (userOrgRole: UserRoleType, capability: OrgCapability) => {
  return ORG_CAPABILITIES_MAP[capability].includes(userOrgRole);
};

export const getOrgCapabilities = (userOrgRole: UserRoleType) => {
  return Object.fromEntries(
    Object.values(OrgCapability).map((capability) => [
      capability,
      hasOrgCapability(userOrgRole, capability),
    ]),
  ) as Record<OrgCapability, boolean>;
};

// Course capabilities
export const hasCourseCapability = (userCourseRole: UserRoleType, capability: CourseCapability) => {
  return COURSE_CAPABILITIES_MAP[capability].includes(userCourseRole);
};

export const getCourseCapabilities = (userCourseRole: UserRoleType) => {
  return Object.fromEntries(
    Object.values(CourseCapability).map((capability) => [
      capability,
      hasCourseCapability(userCourseRole, capability),
    ]),
  ) as Record<CourseCapability, boolean>;
};

// Page capabilities
const getCanViewPage = (page: PagePermissionType | null, userRole: UserRoleType) => {
  const canSeeAllDocs = hasOrgCapability(userRole, Capability.CAN_SEE_ALL_DOCS);
  const { canAuthorSection, canParticipateInSection } = getSectionCapabilities(userRole);
  const isPageReleased = Boolean(page?.released_at);
  return canSeeAllDocs || canAuthorSection || (canParticipateInSection && isPageReleased);
};

const getCanViewOtherUsersData = (page: PagePermissionType | null, userRole: UserRoleType) => {
  const canAuthorSection = hasSectionCapability(userRole, Capability.CAN_AUTHOR_SECTION);
  const canViewPage = getCanViewPage(page, userRole);
  const isPageVisibleToAll =
    canViewPage && Boolean(page?.last_rolled_up_at || page?.class_responses_released_at);
  return canAuthorSection || isPageVisibleToAll;
};

const getCanMakeResponse = (
  page: PagePermissionType | null,
  userRole: UserRoleType,
  readyPageGroup: boolean,
) => {
  if (!page) {
    return false;
  }
  // Only students can make responses
  const canParticipateInSection = hasSectionCapability(
    userRole,
    Capability.CAN_PARTICIPATE_IN_SECTION,
  );
  const canViewPage = getCanViewPage(page, userRole);
  if (!canParticipateInSection || !canViewPage || page.locked_at) {
    return false;
  }
  const isGroupPage =
    page.interaction_mode &&
    [InteractionMode.ASSIGN_STUDENTS, InteractionMode.SELF_ASSIGN].includes(page.interaction_mode);

  // If it's a group page user has to be in a group and ready to make a response
  return !isGroupPage || readyPageGroup;
};

const getCanAuthorPage = (userRole: UserRoleType) => {
  return hasSectionCapability(userRole, Capability.CAN_AUTHOR_SECTION);
};

export const getPageCapabilities = (
  page: PagePermissionType | null,
  userRole: UserRoleType,
  readyPageGroup: boolean,
): Record<PageCapability, boolean> => {
  return {
    [PageCapability.CAN_VIEW_PAGE]: getCanViewPage(page, userRole),
    [PageCapability.CAN_VIEW_OTHER_USERS_DATA]: getCanViewOtherUsersData(page, userRole),
    [PageCapability.CAN_MAKE_RESPONSE]: getCanMakeResponse(page, userRole, readyPageGroup),
    [PageCapability.CAN_AUTHOR_PAGE]: getCanAuthorPage(userRole),
  };
};

export const getPageCapabilitiesForServer = (
  page: PagePermissionType | null,
  userRole: UserRoleType,
) => {
  return {
    [PageCapability.CAN_VIEW_PAGE]: getCanViewPage(page, userRole),
    [PageCapability.CAN_VIEW_OTHER_USERS_DATA]: getCanViewOtherUsersData(page, userRole),
  };
};
